import React, { useState, useEffect, useRef } from 'react';
import siteWash from './siteWash';

/**
 * AeroSitePreview: the preview block (v3 m4). A small browser window (lights
 * and the site's address in the bar) sitting on a wash.
 *
 * With `previewUrl` (a stored snapshot of the real page, DECISIONS
 * 2026-10-25.7) the window shows that image cropped to the top of the page,
 * loaded lazily and faded in at a fixed height, and the wash comes from the
 * snapshot's own colours (`previewColors`, 2 to 3 hex values, via siteWash).
 * Without one it shows its children (the live iframe on a site's page) on
 * the site-tinted wash when `color` is known, else the Publish wash.
 * The capture pipeline that writes previewUrl / previewColors is separate.
 * Flat v3 styles: .aero-spv* in aero-ut-theme.css (render inside .ut-theme).
 *
 * Props: host, previewUrl, previewColors, color, meta, size ('md' | 'sm'), children.
 */
export default function AeroSitePreview({
  host, previewUrl = null, previewColors = null, color = null, meta = null, size = 'md', className = '', children,
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imgRef = useRef(null);
  useEffect(() => { setLoaded(false); setFailed(false); }, [previewUrl]);
  // A cached image can finish loading before React's onLoad is attached, and then onLoad never
  // fires and the picture stays invisible (a blank white window). Look at the image itself.
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
  }, [previewUrl]);
  const shot = previewUrl && !failed;
  const wash = siteWash(shot && previewColors && previewColors.length ? previewColors : color);
  return (
    <div
      className={`aero-spv aero-spv-${size}${wash ? '' : ' aero-w-publish'} ${className}`.trim()}
      style={wash ? { background: wash } : undefined}
    >
      <div className="aero-spv-win">
        <div className="aero-spv-bar">
          <span className="aero-spv-lights" aria-hidden="true"><i /><i /><i /></span>
          {host && <span className="aero-spv-host">{host}</span>}
          {meta && <span className="aero-spv-meta">{meta}</span>}
        </div>
        <div className="aero-spv-body">
          {shot ? (
            <img
              ref={imgRef}
              className={`aero-spv-shot${loaded ? ' is-loaded' : ''}`}
              src={previewUrl}
              alt={host ? `${host}, snapshot of the home page` : 'Snapshot of the home page'}
              loading="lazy"
              decoding="async"
              onLoad={() => setLoaded(true)}
              onError={() => setFailed(true)}
            />
          ) : children}
        </div>
      </div>
    </div>
  );
}
